// Substep 5-λ.2: Task Templates admin page.
//
// Firm admin manages matter templates + their tasks. Before this page,
// templates could only be edited via SQL — this closes the admin gap
// that persisted through Phase 5-η's task-workflow work.
//
// Layout: two-pane. Left = list of templates (name + task count +
// applicable_stages chips + source badge). Right = selected template's
// details (metadata + task list with inline edit).
//
// Backed by the endpoints in Substep 5-λ.1:
//   GET  /api/matter-templates            — list
//   GET  /api/matter-templates/{id}       — full template incl. tasks
//   POST /api/matter-templates            — create
//   PATCH /api/matter-templates/{id}      — update name / stages / etc.
//   DELETE /api/matter-templates/{id}     — delete (except source=stage_seed)
//   POST /api/matter-templates/{id}/tasks             — add task
//   PATCH /api/matter-templates/{id}/tasks/{taskId}   — edit task
//   DELETE /api/matter-templates/{id}/tasks/{taskId}  — delete task

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

// Mirror STAGE_KEYS from app/services/stages.py for the applicable-stages
// picker inside template metadata. Kept consistent with the same list
// in TasksCard / FirmSopsPage. If stages ever go per-firm this becomes
// an API call.
const STAGES = [
  { key: "new_case",    label: "New Case" },
  { key: "treatment",   label: "Treatment" },
  { key: "demand",      label: "Demand" },
  { key: "mediation",   label: "Mediation" },
  { key: "litigation",  label: "Litigation" },
  { key: "settlement",  label: "Settlement" },
];
const STAGE_LABEL_BY_KEY = Object.fromEntries(STAGES.map(s => [s.key, s.label]));

export function TaskTemplatesPage({ showNotification }) {
  const [templates, setTemplates] = useState(null);       // list, null=loading
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);              // full detail of selectedId
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [taskDraft, setTaskDraft] = useState(null);        // draft while editing
  const [addingTask, setAddingTask] = useState(false);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplateForm, setNewTemplateForm] = useState({ name: "", description: "" });

  const loadTemplates = useCallback(async () => {
    try {
      const d = await apiFetch("/api/matter-templates");
      setTemplates(d.data || []);
    } catch (e) {
      showNotification?.(`Failed to load templates: ${e.message}`, "error");
      setTemplates([]);
    }
  }, [showNotification]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const d = await apiFetch(`/api/matter-templates/${id}`);
      setDetail(d);
    } catch (e) {
      showNotification?.(`Failed to load template: ${e.message}`, "error");
      setDetail(null);
    }
    setDetailLoading(false);
  }, [showNotification]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  // ── Template metadata handlers ────────────────────────────────
  const saveMeta = async (updates) => {
    if (!detail) return;
    setSavingMeta(true);
    try {
      await apiFetch(`/api/matter-templates/${detail.id}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
      showNotification?.("Template updated.", "success");
      await Promise.all([loadTemplates(), loadDetail(detail.id)]);
    } catch (e) {
      showNotification?.(`Save failed: ${e.message}`, "error");
    }
    setSavingMeta(false);
  };

  const deleteTemplate = async () => {
    if (!detail) return;
    if (!window.confirm(
      `Delete template "${detail.name}"?\n\nThis removes the template and its tasks. Already-applied tasks on open matters are NOT affected.`
    )) return;
    try {
      await apiFetch(`/api/matter-templates/${detail.id}`, { method: "DELETE" });
      showNotification?.("Template deleted.", "success");
      setSelectedId(null);
      setDetail(null);
      await loadTemplates();
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, "error");
    }
  };

  const createTemplate = async () => {
    if (!newTemplateForm.name.trim()) return;
    try {
      const resp = await apiFetch("/api/matter-templates", {
        method: "POST",
        body: JSON.stringify({
          name: newTemplateForm.name.trim(),
          description: newTemplateForm.description.trim() || null,
        }),
      });
      showNotification?.(`Template "${resp.name}" created.`, "success");
      setShowNewTemplate(false);
      setNewTemplateForm({ name: "", description: "" });
      await loadTemplates();
      setSelectedId(resp.id);
    } catch (e) {
      showNotification?.(`Create failed: ${e.message}`, "error");
    }
  };

  // ── Task handlers ─────────────────────────────────────────────
  const openTaskEdit = (task) => {
    setEditingTaskId(task.id);
    setTaskDraft({
      title: task.title || "",
      body: task.body || "",
      sort_order: task.sort_order,
      default_assignee_role: task.default_assignee_role || "",
      due_offset_days: task.due_offset_days ?? "",
    });
  };
  const cancelTaskEdit = () => { setEditingTaskId(null); setTaskDraft(null); };

  const saveTask = async () => {
    if (!detail || editingTaskId == null || !taskDraft) return;
    if (!taskDraft.title.trim()) {
      showNotification?.("Task title is required.", "warning");
      return;
    }
    try {
      const body = {
        title: taskDraft.title.trim(),
        body: taskDraft.body,
        sort_order: parseInt(taskDraft.sort_order, 10) || 0,
        default_assignee_role: taskDraft.default_assignee_role.trim() || null,
        due_offset_days: taskDraft.due_offset_days === ""
          ? null : parseInt(taskDraft.due_offset_days, 10),
      };
      await apiFetch(`/api/matter-templates/${detail.id}/tasks/${editingTaskId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      showNotification?.("Task updated.", "success");
      cancelTaskEdit();
      await loadDetail(detail.id);
    } catch (e) {
      showNotification?.(`Save failed: ${e.message}`, "error");
    }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?\n\nThis only affects future matters using this template.`)) return;
    try {
      await apiFetch(`/api/matter-templates/${detail.id}/tasks/${task.id}`, { method: "DELETE" });
      showNotification?.("Task deleted.", "success");
      await loadDetail(detail.id);
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, "error");
    }
  };

  const addTask = async () => {
    setAddingTask(true);
    try {
      const resp = await apiFetch(`/api/matter-templates/${detail.id}/tasks`, {
        method: "POST",
        body: JSON.stringify({ title: "New task" }),
      });
      showNotification?.("Task added.", "success");
      await loadDetail(detail.id);
      // Immediately open the new row for edit so the user can rename
      // "New task" to the real title without an extra click.
      openTaskEdit({ id: resp.id, title: "New task", body: "",
                     sort_order: resp.sort_order,
                     default_assignee_role: "", due_offset_days: null });
    } catch (e) {
      showNotification?.(`Add failed: ${e.message}`, "error");
    }
    setAddingTask(false);
  };

  return React.createElement("div", { className: "fade-in", style: { padding: "32px 40px", maxWidth: "1400px" } },
    // ─── Header ─────────────────────────────────────────────
    React.createElement("div", { style: { marginBottom: "24px" } },
      React.createElement("h1", {
        style: { fontFamily: "'Playfair Display',serif", fontSize: "28px", fontWeight: 600, color: C.navy, marginBottom: "6px" },
      }, "Task Templates"),
      React.createElement("p", { style: { fontSize: "14px", color: C.textMuted, maxWidth: "820px" } },
        "Task-list templates applied to matters. Edit the tasks here to change what shows up for future matters — already-applied tasks on open matters aren't touched. Stage-milestone templates (auto-generated) can be edited but not deleted.",
      ),
    ),

    // ─── Two-pane layout ────────────────────────────────────
    React.createElement("div", {
      style: { display: "grid", gridTemplateColumns: "320px 1fr", gap: "20px",
               alignItems: "start" }
    },
      // ── Left: template list ──
      React.createElement("div", null,
        React.createElement("div", {
          style: {
            background: "#fff", borderRadius: "12px",
            border: `1px solid ${C.warmGray}`, overflow: "hidden",
          }
        },
          React.createElement("div", {
            style: {
              padding: "12px 14px", borderBottom: `1px solid ${C.warmGray}`,
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }
          },
            React.createElement("h2", {
              style: { fontSize: "12px", color: C.textMuted, textTransform: "uppercase",
                       letterSpacing: "0.06em", fontWeight: 700, margin: 0 }
            }, `Templates${templates ? ` (${templates.length})` : ""}`),
            React.createElement("button", {
              onClick: () => setShowNewTemplate(true),
              style: {
                padding: "4px 10px", borderRadius: "6px", border: "none",
                background: C.gold, color: "#fff",
                fontSize: "11px", fontWeight: 600, cursor: "pointer",
              }
            }, "+ New"),
          ),
          templates === null
            ? React.createElement("div", { style: { padding: "20px", textAlign: "center" } }, React.createElement(Spinner, null))
            : templates.length === 0
              ? React.createElement("div", { style: { padding: "20px", color: C.textMuted, fontSize: "12px", textAlign: "center" } },
                  "No templates yet.")
              : React.createElement("div", null,
                  ...templates.map(t => React.createElement("button", {
                    key: t.id,
                    onClick: () => setSelectedId(t.id),
                    style: {
                      width: "100%", textAlign: "left", padding: "10px 14px",
                      background: selectedId === t.id ? `${C.gold}18` : "transparent",
                      border: "none",
                      borderLeft: selectedId === t.id ? `3px solid ${C.gold}` : "3px solid transparent",
                      borderBottom: `1px solid ${C.warmGray}40`,
                      cursor: "pointer", display: "flex", flexDirection: "column", gap: "3px",
                    },
                    onMouseEnter: e => { if (selectedId !== t.id) e.currentTarget.style.background = C.warmWhite; },
                    onMouseLeave: e => { if (selectedId !== t.id) e.currentTarget.style.background = "transparent"; },
                  },
                    React.createElement("div", {
                      style: { fontSize: "13px", fontWeight: 600, color: C.navy }
                    }, t.name),
                    React.createElement("div", {
                      style: { fontSize: "11px", color: C.textMuted, display: "flex", gap: "6px", flexWrap: "wrap" }
                    },
                      React.createElement("span", null, `${t.task_count} tasks`),
                      t.source === "stage_seed" && React.createElement("span", {
                        style: { color: C.gold, fontWeight: 600 }
                      }, "· milestone"),
                      !t.is_active && React.createElement("span", {
                        style: { color: C.error, fontWeight: 600 }
                      }, "· inactive"),
                    ),
                    (t.applicable_stages || []).length > 0 && React.createElement("div", {
                      style: { display: "flex", gap: "3px", flexWrap: "wrap", marginTop: "2px" }
                    },
                      ...(t.applicable_stages || []).map(k => React.createElement("span", {
                        key: k,
                        style: {
                          fontSize: "9px", padding: "1px 5px", borderRadius: "999px",
                          background: `${C.gold}22`, color: C.textDark, fontWeight: 600,
                        }
                      }, STAGE_LABEL_BY_KEY[k] || k)),
                    ),
                  )),
                ),
        ),

        // Inline new-template form
        showNewTemplate && React.createElement("div", {
          style: {
            marginTop: "12px", padding: "14px",
            background: "#fff", borderRadius: "12px",
            border: `1px solid ${C.warmGray}`,
          }
        },
          React.createElement("h3", { style: { fontSize: "12px", color: C.navy, margin: "0 0 8px 0", fontWeight: 700 } }, "New template"),
          React.createElement("input", {
            type: "text", placeholder: "Template name",
            value: newTemplateForm.name,
            onChange: e => setNewTemplateForm(f => ({ ...f, name: e.target.value })),
            style: { width: "100%", padding: "7px 10px", border: `1px solid ${C.warmGray}`,
                     borderRadius: "6px", fontSize: "13px", boxSizing: "border-box", marginBottom: "6px" }
          }),
          React.createElement("textarea", {
            placeholder: "Description (optional)",
            value: newTemplateForm.description,
            onChange: e => setNewTemplateForm(f => ({ ...f, description: e.target.value })),
            rows: 2,
            style: { width: "100%", padding: "7px 10px", border: `1px solid ${C.warmGray}`,
                     borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
                     fontFamily: "inherit", resize: "vertical", marginBottom: "8px" }
          }),
          React.createElement("div", { style: { display: "flex", gap: "6px", justifyContent: "flex-end" } },
            React.createElement("button", {
              onClick: () => { setShowNewTemplate(false); setNewTemplateForm({ name: "", description: "" }); },
              style: { padding: "5px 12px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                       background: "#fff", color: C.textMuted, fontSize: "11px", cursor: "pointer" }
            }, "Cancel"),
            React.createElement("button", {
              onClick: createTemplate,
              disabled: !newTemplateForm.name.trim(),
              style: { padding: "5px 14px", borderRadius: "6px", border: "none",
                       background: newTemplateForm.name.trim() ? C.gold : C.warmGray,
                       color: "#fff", fontSize: "11px", fontWeight: 600, cursor: "pointer" }
            }, "Create"),
          ),
        ),
      ),

      // ── Right: detail pane ──
      React.createElement("div", null,
        !selectedId
          ? React.createElement("div", {
              style: { background: "#fff", borderRadius: "12px", padding: "40px",
                       border: `1px solid ${C.warmGray}`, textAlign: "center",
                       color: C.textMuted, fontSize: "13px" }
            }, "Select a template from the left to view + edit its tasks.")
          : detailLoading || !detail
            ? React.createElement("div", {
                style: { background: "#fff", borderRadius: "12px", padding: "40px",
                         border: `1px solid ${C.warmGray}`, textAlign: "center" }
              }, React.createElement(Spinner, null))
            : _renderTemplateDetail(detail, {
                savingMeta, editingTaskId, taskDraft, addingTask,
                saveMeta, deleteTemplate, addTask,
                openTaskEdit, cancelTaskEdit, saveTask, deleteTask,
                setTaskDraft,
              }),
      ),
    ),
  );
}


function _renderTemplateDetail(t, opts) {
  const {
    savingMeta, editingTaskId, taskDraft, addingTask,
    saveMeta, deleteTemplate, addTask,
    openTaskEdit, cancelTaskEdit, saveTask, deleteTask,
    setTaskDraft,
  } = opts;

  const isSystem = t.source === "stage_seed";
  const stages = t.applicable_stages || [];

  return React.createElement("div", null,
    // ── Metadata card ──
    React.createElement("div", {
      style: { background: "#fff", borderRadius: "12px", padding: "20px 24px",
               border: `1px solid ${C.warmGray}`, marginBottom: "16px" }
    },
      React.createElement("div", {
        style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                 gap: "12px", marginBottom: "12px" }
      },
        React.createElement("div", { style: { flex: 1 } },
          React.createElement("h2", {
            style: { fontFamily: "'Playfair Display',serif", fontSize: "22px",
                     color: C.navy, margin: "0 0 4px 0" }
          }, t.name),
          React.createElement("div", { style: { fontSize: "12px", color: C.textMuted,
                                                display: "flex", gap: "10px", flexWrap: "wrap" } },
            React.createElement("span", null, `${(t.tasks || []).length} tasks`),
            isSystem && React.createElement("span", { style: { color: C.gold, fontWeight: 600 } }, "· stage-milestone (system)"),
            React.createElement("span", null, `· source: ${t.source}`),
            !t.is_active && React.createElement("span", { style: { color: C.error } }, "· inactive"),
          ),
        ),
        !isSystem && React.createElement("button", {
          onClick: deleteTemplate,
          style: { padding: "6px 12px", borderRadius: "6px",
                   border: `1px solid ${C.warmGray}`, background: "#fff",
                   color: C.error, fontSize: "12px", cursor: "pointer", flexShrink: 0 }
        }, "Delete template"),
      ),

      // Stages
      React.createElement("div", { style: { marginBottom: "10px" } },
        React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginBottom: "6px",
                                              fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" } },
          "Applies to stages"),
        React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: "6px" } },
          ...STAGES.map(s => {
            const on = stages.includes(s.key);
            return React.createElement("label", {
              key: s.key,
              style: {
                display: "flex", alignItems: "center", gap: "5px",
                padding: "3px 10px", borderRadius: "999px", cursor: savingMeta ? "wait" : "pointer",
                background: on ? C.navy : "#fff",
                color: on ? "#fff" : C.textDark,
                border: `1px solid ${on ? C.navy : C.warmGray}`,
                fontSize: "12px", userSelect: "none",
                opacity: savingMeta ? 0.6 : 1,
              }
            },
              React.createElement("input", {
                type: "checkbox",
                checked: on,
                disabled: savingMeta,
                onChange: () => {
                  const next = on ? stages.filter(k => k !== s.key) : [...stages, s.key];
                  saveMeta({ applicable_stages: next });
                },
                style: { margin: 0 },
              }),
              s.label,
            );
          }),
        ),
        stages.length === 0 && React.createElement("div", {
          style: { fontSize: "11px", color: C.textLight, marginTop: "6px", fontStyle: "italic" }
        }, "No stages selected → cross-cutting (surfaces on every stage's matter view)."),
      ),

      // Name + description edit (inline)
      React.createElement("details", { style: { marginTop: "8px" } },
        React.createElement("summary", {
          style: { fontSize: "11px", color: C.textMuted, cursor: "pointer",
                   userSelect: "none", padding: "4px 0" }
        }, "▸ Edit name / description"),
        React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" } },
          React.createElement("input", {
            type: "text", defaultValue: t.name,
            onBlur: e => { if (e.target.value.trim() && e.target.value !== t.name) saveMeta({ name: e.target.value.trim() }); },
            placeholder: "Template name",
            style: { padding: "7px 10px", border: `1px solid ${C.warmGray}`, borderRadius: "6px",
                     fontSize: "13px", boxSizing: "border-box", width: "100%" }
          }),
          React.createElement("textarea", {
            defaultValue: t.description || "",
            onBlur: e => { if (e.target.value !== (t.description || "")) saveMeta({ description: e.target.value }); },
            placeholder: "Description",
            rows: 2,
            style: { padding: "7px 10px", border: `1px solid ${C.warmGray}`, borderRadius: "6px",
                     fontSize: "13px", boxSizing: "border-box", width: "100%",
                     fontFamily: "inherit", resize: "vertical" }
          }),
          React.createElement("div", { style: { display: "flex", gap: "6px", alignItems: "center" } },
            React.createElement("label", {
              style: { display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: C.textDark }
            },
              React.createElement("input", {
                type: "checkbox",
                defaultChecked: !!t.is_active,
                onChange: e => saveMeta({ is_active: e.target.checked }),
              }),
              "Active",
            ),
            React.createElement("label", {
              style: { display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: C.textDark, marginLeft: "16px" }
            },
              React.createElement("input", {
                type: "checkbox",
                defaultChecked: !!t.auto_apply_on_matter_created,
                onChange: e => saveMeta({ auto_apply_on_matter_created: e.target.checked }),
              }),
              "Auto-apply on matter created",
            ),
          ),
        ),
      ),
    ),

    // ── Tasks card ──
    React.createElement("div", {
      style: { background: "#fff", borderRadius: "12px", padding: "20px 24px",
               border: `1px solid ${C.warmGray}` }
    },
      React.createElement("div", {
        style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }
      },
        React.createElement("h3", {
          style: { fontSize: "13px", color: C.textMuted, textTransform: "uppercase",
                   letterSpacing: "0.06em", fontWeight: 700, margin: 0 }
        }, "Tasks"),
        React.createElement("button", {
          onClick: addTask,
          disabled: addingTask,
          style: { padding: "5px 12px", borderRadius: "6px", border: "none",
                   background: addingTask ? C.warmGray : C.gold, color: "#fff",
                   fontSize: "12px", fontWeight: 600, cursor: addingTask ? "default" : "pointer" }
        }, addingTask ? "Adding…" : "+ Add task"),
      ),
      (t.tasks || []).length === 0
        ? React.createElement("div", {
            style: { padding: "20px", textAlign: "center", color: C.textMuted, fontSize: "12px" }
          }, "No tasks yet. Add one above.")
        : React.createElement("div", {
            style: { display: "flex", flexDirection: "column",
                     border: `1px solid ${C.warmGray}`, borderRadius: "8px", overflow: "hidden" }
          },
            ...(t.tasks || []).map((task, i) => editingTaskId === task.id
              ? _renderTaskEditor(task, taskDraft, setTaskDraft, saveTask, cancelTaskEdit, i === 0)
              : _renderTaskRow(task, i === 0, () => openTaskEdit(task), () => deleteTask(task)),
            ),
          ),
    ),
  );
}


function _renderTaskRow(task, first, onEdit, onDelete) {
  return React.createElement("div", {
    key: task.id,
    style: {
      padding: "10px 14px", background: "#fff",
      borderTop: first ? "none" : `1px solid ${C.warmGray}`,
      display: "flex", flexDirection: "column", gap: "4px",
    }
  },
    React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: "10px" }
    },
      React.createElement("code", {
        style: { fontSize: "11px", color: C.textMuted, background: C.warmWhite,
                 padding: "1px 6px", borderRadius: "4px", minWidth: "40px", textAlign: "center" },
        title: "Sort order",
      }, task.sort_order),
      React.createElement("span", {
        style: { fontSize: "13px", fontWeight: 600, color: C.navy, flex: 1 }
      }, task.title),
      task.default_assignee_role && React.createElement("span", {
        style: { fontSize: "10px", padding: "2px 8px", borderRadius: "999px",
                 background: `${C.navy}12`, color: C.navy, fontWeight: 600 }
      }, task.default_assignee_role),
      task.due_offset_days != null && React.createElement("span", {
        style: { fontSize: "10px", color: C.textMuted },
        title: "Days from apply-date",
      }, `+${task.due_offset_days}d`),
      task.sop_id && React.createElement("span", {
        style: { fontSize: "10px", color: C.gold, fontWeight: 600 },
        title: `Linked to SOP #${task.sop_id}`,
      }, `★ SOP`),
      task.auto_complete_trigger && React.createElement("span", {
        style: { fontSize: "10px", color: C.success, fontWeight: 600 },
        title: "Milestone task — auto-completes on wired event",
      }, "auto"),
      React.createElement("div", { style: { display: "flex", gap: "4px" } },
        React.createElement("button", {
          onClick: onEdit,
          style: { padding: "3px 10px", borderRadius: "6px",
                   border: `1px solid ${C.warmGray}`, background: "#fff",
                   color: C.textDark, fontSize: "11px", cursor: "pointer" }
        }, "Edit"),
        React.createElement("button", {
          onClick: onDelete,
          style: { padding: "3px 10px", borderRadius: "6px",
                   border: `1px solid ${C.warmGray}`, background: "#fff",
                   color: C.error, fontSize: "11px", cursor: "pointer" }
        }, "Delete"),
      ),
    ),
    task.body && React.createElement("div", {
      style: { fontSize: "12px", color: C.textMuted, whiteSpace: "pre-wrap",
               paddingLeft: "50px", opacity: 0.85 }
    }, task.body.length > 200 ? task.body.slice(0, 200) + "…" : task.body),
  );
}


function _renderTaskEditor(task, draft, setDraft, save, cancel, first) {
  if (!draft) return null;
  const set = (k) => (e) => setDraft(d => ({ ...d, [k]: e.target.value }));
  return React.createElement("div", {
    key: task.id,
    style: {
      padding: "14px", background: C.warmWhite,
      borderTop: first ? "none" : `1px solid ${C.warmGray}`,
      display: "flex", flexDirection: "column", gap: "8px",
    }
  },
    React.createElement("div", { style: { display: "grid", gridTemplateColumns: "80px 1fr", gap: "8px", alignItems: "center" } },
      React.createElement("input", {
        type: "number", value: draft.sort_order, onChange: set("sort_order"),
        style: { padding: "6px 8px", border: `1px solid ${C.warmGray}`, borderRadius: "6px",
                 fontSize: "13px", boxSizing: "border-box", fontFamily: "monospace" }
      }),
      React.createElement("input", {
        type: "text", value: draft.title, onChange: set("title"),
        placeholder: "Task title",
        style: { padding: "6px 10px", border: `1px solid ${C.warmGray}`, borderRadius: "6px",
                 fontSize: "13px", boxSizing: "border-box" }
      }),
    ),
    React.createElement("textarea", {
      value: draft.body, onChange: set("body"),
      placeholder: "Body (instructions, sub-steps, etc.)",
      rows: 4,
      style: { padding: "8px 10px", border: `1px solid ${C.warmGray}`, borderRadius: "6px",
               fontSize: "12px", boxSizing: "border-box", fontFamily: "monospace",
               resize: "vertical", lineHeight: 1.5 }
    }),
    React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" } },
      React.createElement("input", {
        type: "text", value: draft.default_assignee_role, onChange: set("default_assignee_role"),
        placeholder: "Default assignee role (e.g. paralegal)",
        style: { padding: "6px 10px", border: `1px solid ${C.warmGray}`, borderRadius: "6px",
                 fontSize: "12px", boxSizing: "border-box" }
      }),
      React.createElement("input", {
        type: "number", value: draft.due_offset_days, onChange: set("due_offset_days"),
        placeholder: "Due offset (days)",
        style: { padding: "6px 10px", border: `1px solid ${C.warmGray}`, borderRadius: "6px",
                 fontSize: "12px", boxSizing: "border-box" }
      }),
    ),
    React.createElement("div", { style: { display: "flex", justifyContent: "flex-end", gap: "6px" } },
      React.createElement("button", {
        onClick: cancel,
        style: { padding: "6px 14px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                 background: "#fff", color: C.textMuted, fontSize: "12px", cursor: "pointer" }
      }, "Cancel"),
      React.createElement("button", {
        onClick: save,
        style: { padding: "6px 18px", borderRadius: "6px", border: "none",
                 background: C.gold, color: "#fff", fontSize: "12px", fontWeight: 600, cursor: "pointer" }
      }, "Save"),
    ),
  );
}
